import propTypes from "prop-types";
import React, { useState } from "react";

import Logo from "images/gvi-logo2.inline.svg";

import { useBreakpoint } from "../../hooks/useBreakpoint";
import ProgramCard from "../cards/programCard";
import FilterBox from "../filterBox";
import CtaButton from "../shared/buttons/ctaButton";
import { ConditionalSwiper } from "../shared/conditionalSwiper";
import { FilterMenuContainer } from "./filterMenuContainer";

const ProgramListHeader = ({ subtitle, title }) => {
  return (
    <>
      <div className="flex items-center">
        <Logo className="w-8 fill-black-lighter dark:fill-white" />
        <span className="text-white pl-4 uppercase h7 !font-semibold menu-link">
          {subtitle}
        </span>
      </div>

      <h2
        className="pt-0 pb-4 heading dark:pl-0 md:pt-2"
        dangerouslySetInnerHTML={{ __html: title }}
      />
    </>
  );
};

/**
 *  NOTES:
 *
 *  @description conservation vs community programs are relative to the brand that you on: while on people, we don't want the community programs to show :)
 *
 *    on planet
 *      > conservation programs = planet programs
 *      > community programs = people programs
 *    on people
 *      > conservation programs = people programs
 *      > community programs = planet programs
 *
 */

const ProgramList = ({
  title,
  conservationPrograms,
  communityPrograms,
  sortByType = false,
  filterCallback,
  filterCommunityCallback,
  paginationCallback,
  pagination,
  filterCommunityOptions,
  filterBoxHide,
  filterOptions,
  mobileFilterOptions,
  mobileCommunityFilterOptions,
  programFilterMobileMenuCTAClass = "",
  sectionContainerDefaultClass = "flex flex-col px-4 sm:mt-14 md:mt-0 md:px-0",
  sectionContainerClass = "bg-secondary-bg md:px-0 dark:gvi-light-gray-bg",
  titleHeadingClass = "font-light text-left md:text-center text-generic-text dark:text-white",
  titleDefaultStyle = "flex flex-col justify-center w-full h-24 py-2 mx-auto md:w-9/12 md:-mt-12",
  titleStyle = "md:bg-gray-tertiary dark:md:bg-gray-standard",
  viewMoreText = "View more",
  sectionPaginationGradientDefaultClass = "before:absolute before:w-full before:h-28 before:bottom-8 before:z-20 before:masked-overview before:masked-overview-top",
  sectionPaginationGradientClass = "before:bg-yellow-lighter dark:before:bg-gray-darker",
  programlistOuterContainerClass = "programs-list-outer-container md:pt-8 pb-10 md:pb-36",
  programlistInnerContainerClass = "programs-list-inner-container relative flex flex-col justify-between gap-14 md:gap-28 md:flex-row md:mt-28",
  programlistPrimaryProgramsContainer = "conservation-programs-list w-full py-2 ",
  programlistPrimarySecondaryContainer = "community-programs-list w-full py-2 sm:pb-9 ",
  programlistPrimaryProgramsSliderContainer = "",
  programlistPrimarySecondarySliderContainer = "",
  primaryCtaLabel = "Explore all programs",
  secondaryCtaLabel = "Compare programs",
  primaryCtaLink = "/find-a-program",
  secondaryCtaLink = "/program-comparison/",
  primaryCtaContainer = "block sm:hidden py-4 mx-auto w-full sm:w-1/2 flex flex-col sm:flex-row items-center sm:space-x-10 h-full"
}) => {
  const breakpoint = useBreakpoint();

  const [initialPrograms, setInitialPrograms] = useState({
    conservationCount: conservationPrograms?.length ?? 0,
    communityCount: communityPrograms?.length ?? 0
  });

  if (
    (initialPrograms?.conservationCount < 1 &&
      initialPrograms?.communityCount < 1) ||
    (!sortByType && initialPrograms?.communityCount < 1)
  ) {
    return <></>;
  }

  return (
    <>
      {(initialPrograms?.conservationCount > 0 ||
        initialPrograms?.communityCount > 0) && (
        <section
          className={`${sectionContainerDefaultClass} ${sectionContainerClass}`}
          id="view-programs"
        >
          <div className="container relative w-full mx-auto md:w-4/5">
            <div className="relative flex flex-col justify-center w-full md:absolute">
              <div className={`${titleDefaultStyle} ${titleStyle}`}>
                <h1
                  className={titleHeadingClass}
                  dangerouslySetInnerHTML={{
                    __html: title
                  }}
                />
              </div>
            </div>
            <div className={programlistOuterContainerClass}>
              <div
                className={`${programlistInnerContainerClass}
               ${
                 pagination && breakpoint !== "xs"
                   ? `${sectionPaginationGradientDefaultClass} ${sectionPaginationGradientClass}`
                   : ""
               }`}
              >
                {initialPrograms?.conservationCount > 0 && (
                  <div
                    className={`${programlistPrimaryProgramsContainer} ${
                      sortByType ? " md:w-1/2" : ""
                    }`}
                  >
                    {sortByType && (
                      <>
                        {!filterBoxHide && (
                          <div className="hidden md:block">
                            <FilterBox
                              filterLabel="Apply filter"
                              options={filterOptions}
                              filterCallback={filterCallback}
                              type="no-back"
                            />
                          </div>
                        )}
                        <ProgramListHeader
                          subtitle="Planet"
                          title="Conservation"
                        />
                      </>
                    )}
                    {!filterBoxHide && (
                      <div className="md:hidden pt-4">
                        <FilterMenuContainer
                          filterMenuCTAClass={programFilterMobileMenuCTAClass}
                          filterOptions={mobileFilterOptions}
                          filterPrograms={filterCallback}
                        />
                      </div>
                    )}

                    <div
                      className={`mt-10 md:mt-0 md:max-h-max md:h-auto md:pr-0 md:pt-1 ${
                        sortByType
                          ? "h-[380px] sm:h-[452px] overflow-y-scroll overflow-x-hidden gvi-scrollbar-thin md:!overflow-x-visible md:overflow-visible sm:pr-6"
                          : "md:grid md:grid-cols-2 md:gap-x-28 md:gap-y-4"
                      } ${programlistPrimaryProgramsSliderContainer}`}
                    >
                      <ConditionalSwiper
                        condition={breakpoint === "xs"}
                        slidesPerView={1.15}
                        spaceBetween={20}
                        centeredSlides={false}
                        className="h-full w-full"
                      >
                        {conservationPrograms &&
                          conservationPrograms.length > 0 &&
                          conservationPrograms.map((program, idx) => (
                            <ProgramCard
                              program={program}
                              key={idx}
                              descWidth="w-2/3"
                            />
                          ))}
                      </ConditionalSwiper>
                    </div>
                  </div>
                )}

                {sortByType && initialPrograms?.communityCount > 0 && (
                  <div
                    className={`${programlistPrimarySecondaryContainer} md:w-1/2`}
                  >
                    <>
                      {!filterBoxHide && (
                        <div className="hidden md:block">
                          <FilterBox
                            filterLabel="Apply filter"
                            options={filterCommunityOptions}
                            filterCallback={filterCommunityCallback}
                            type="no-back"
                          />
                        </div>
                      )}
                      {initialPrograms?.conservationCount > 0 && (
                        <ProgramListHeader
                          subtitle="People"
                          title="Community Development"
                        />
                      )}

                      {!filterBoxHide && (
                        <div className="md:hidden pt-4">
                          <FilterMenuContainer
                            filterOptions={mobileCommunityFilterOptions}
                            filterPrograms={filterCommunityCallback}
                          />
                        </div>
                      )}
                      <div
                        className={`mt-10 sm:mt-0 sm:pr-6 md:pr-0 md:pt-1 md:h-auto ${
                          sortByType && initialPrograms?.conservationCount > 0
                            ? "h-[400px] sm:h-[452px] overflow-y-scroll overflow-x-hidden gvi-scrollbar-thin md:!overflow-x-visible md:overflow-visible"
                            : "grid-cols-1 md:grid-cols-2 grid md:gap-x-8 md:gap-y-4"
                        } ${programlistPrimarySecondarySliderContainer}`}
                      >
                        <ConditionalSwiper
                          condition={breakpoint === "xs"}
                          slidesPerView={1.15}
                          spaceBetween={20}
                          centeredSlides={false}
                          className="h-full w-full"
                        >
                          {communityPrograms &&
                            communityPrograms.length > 0 &&
                            communityPrograms.map((program, idx) => (
                              <ProgramCard
                                program={program}
                                extLink={true}
                                key={`program-card-${idx}`}
                              />
                            ))}
                        </ConditionalSwiper>
                      </div>
                    </>
                  </div>
                )}
              </div>
              {/* Load more CTA*/}
              {pagination && breakpoint !== "xs" && (
                <div className="static md:absolute w-full pt-6 pb-8 md:py-0 bottom-20 hidden sm:block">
                  <CtaButton
                    preset={"secondary"}
                    ctaLabel={viewMoreText}
                    labelClass="text-base leading-6 font-bold pr-2"
                    showAsButton={true}
                    extraButtonStyle={"sm:w-48 sm:mx-auto"}
                    arrowClass={"rotate-90"}
                    onClick={paginationCallback}
                  />
                </div>
              )}

              {(primaryCtaLabel || primaryCtaLink) && (
                <div className={primaryCtaContainer}>
                  <CtaButton
                    preset={"primary"}
                    ctaLabel={primaryCtaLabel}
                    labelClass="text-base leading-genericCta font-bold pr-2"
                    extraButtonStyle={"w-full mb-4 sm:mb-0"}
                    to={primaryCtaLink}
                  />
                  <CtaButton
                    preset={"secondary"}
                    ctaLabel={secondaryCtaLabel}
                    extraButtonStyle={"w-full sm:flex"}
                    labelClass="text-base leading-genericCta font-bold pr-2"
                    to={secondaryCtaLink}
                    showAsButton={false}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default ProgramList;

ProgramList.propTypes = {
  primaryCtaLabel: propTypes.string,
  primaryCtaLink: propTypes.string,
  secondaryCtaLink: propTypes.string,
  secondaryCtaLabel: propTypes.string
};
